import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { ArrowLeft, Printer, DownloadSimple, PencilSimple } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ViewDocument({ apiPath, numberField, editPath, listPath, docLabel }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dRes = await axios.get(`${API}/${apiPath}/${id}`);
        setDoc(dRes.data);
        const cRes = await axios.get(`${API}/companies`);
        setCompany(cRes.data.find(c => c.id === dRes.data.company_id));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [id, apiPath]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: doc ? `${docLabel}-${doc[numberField]}` : docLabel,
  });

  const handlePDFDownload = () => {
    window.open(`${API}/${apiPath}/${id}/pdf`, '_blank');
  };

  if (loading) return <div className="py-20 text-center text-stone-400 text-sm">Loading...</div>;
  if (!doc) return <div className="text-center"><p className="text-stone-400 mb-4">{docLabel} not found</p></div>;

  const q = doc;

  return (
    <div>
      <div className="no-print mb-6">
        <button onClick={() => navigate(listPath)}
          className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-800 text-sm font-medium mb-6 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h1 className="text-3xl text-stone-900">{q[numberField]}</h1>
          <div className="flex flex-wrap gap-2">
            <Link to={`${editPath}/${id}`}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-[#002FA7] bg-[#002FA7]/5 hover:bg-[#002FA7]/10 transition-all">
              <PencilSimple size={16} /> Edit
            </Link>
            <button onClick={handlePrint}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-stone-700 bg-stone-100 hover:bg-stone-200 transition-all">
              <Printer size={16} /> Print
            </button>
            <button onClick={handlePDFDownload}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 text-white bg-[#002FA7] hover:bg-[#002480] transition-all">
              <DownloadSimple size={16} /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div ref={printRef} className="bg-white border border-stone-200 p-8 sm:p-10 print:border-0 print:p-0">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 pb-4 border-b-2 border-stone-900">
          <div>
            <h2 className="text-xl font-bold text-stone-900">{q[numberField]}</h2>
            <p className="text-sm text-stone-500">Date: {q.date}</p>
          </div>
          <div className="text-right">
            {company && <p className="font-bold text-stone-900">{company.name}</p>}
            {company?.gst_number && <p className="text-xs text-stone-500">GST: {company.gst_number}</p>}
          </div>
        </div>

        {/* Customer / Vendor */}
        {q.customer?.name && (
          <div className="mb-5">
            <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2">
              {apiPath === 'purchase-orders' ? 'Vendor' : 'To'}
            </p>
            <p className="font-semibold text-stone-900">{q.customer.name}</p>
            {q.customer.address && <p className="text-sm text-stone-600">{q.customer.address}</p>}
            <div className="text-sm text-stone-600">
              {q.customer.contact && <span>Contact: {q.customer.contact}</span>}
              {q.customer.email && <span className="ml-3">Email: {q.customer.email}</span>}
            </div>
            {q.customer.gst_number && <p className="text-sm text-stone-600">GST: {q.customer.gst_number}</p>}
          </div>
        )}

        {/* Bill To / Ship To (Purchase Order only) */}
        {apiPath === 'purchase-orders' && (q.bill_to?.name || q.ship_to?.name) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5 p-4 border border-stone-200 bg-stone-50">
            {q.bill_to?.name && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">Bill To</p>
                <p className="font-semibold text-stone-900 text-sm">{q.bill_to.name}</p>
                {q.bill_to.address && <p className="text-xs text-stone-600">{q.bill_to.address}</p>}
                {q.bill_to.contact && <p className="text-xs text-stone-600">Contact: {q.bill_to.contact}</p>}
                {q.bill_to.gst_number && <p className="text-xs text-stone-600">GST: {q.bill_to.gst_number}</p>}
              </div>
            )}
            {q.ship_to?.name && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-1">Ship To</p>
                <p className="font-semibold text-stone-900 text-sm">{q.ship_to.name}</p>
                {q.ship_to.address && <p className="text-xs text-stone-600">{q.ship_to.address}</p>}
                {q.ship_to.contact && <p className="text-xs text-stone-600">Contact: {q.ship_to.contact}</p>}
                {q.ship_to.gst_number && <p className="text-xs text-stone-600">GST: {q.ship_to.gst_number}</p>}
              </div>
            )}
          </div>
        )}

        {q.subject && <p className="text-sm font-semibold text-stone-800 mb-4">Sub.: {q.subject}</p>}

        {/* Items Table */}
        <div className="mb-5 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-y-2 border-stone-900 bg-stone-50">
                <th className="py-2 px-2 text-left font-bold text-stone-800 text-xs w-[40px]">S.No.</th>
                <th className="py-2 px-2 text-left font-bold text-stone-800 text-xs">Description</th>
                <th className="py-2 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Qty.</th>
                <th className="py-2 px-2 text-center font-bold text-stone-800 text-xs w-[50px]">Unit</th>
                <th className="py-2 px-2 text-right font-bold text-stone-800 text-xs w-[100px]">Rate (Rs.)</th>
                <th className="py-2 px-2 text-right font-bold text-stone-800 text-xs w-[110px]">Amount (Rs.)</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((item, idx) => (
                <tr key={`item-${item.product_id}-${idx}`} className="border-b border-stone-200 align-top">
                  <td className="py-2 px-2 text-stone-600">{idx + 1}</td>
                  <td className="py-2 px-2">
                    <p className="font-semibold text-stone-900">{item.product_name}</p>
                    {item.description && <p className="text-xs text-stone-600">{item.description}</p>}
                    {item.hsn_code && <p className="text-xs text-stone-500">HSN: {item.hsn_code}</p>}
                    {item.features && item.features.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {item.features.map((f, fi) => f.trim() && (
                          <li key={`feat-${fi}`} className="flex items-start gap-1.5">
                            <span className="text-stone-400 text-xs mt-0.5">&bull;</span>
                            <span className="text-xs text-stone-600">{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {item.warranty && <p className="text-xs text-stone-500 mt-1">Warranty: {item.warranty}</p>}
                  </td>
                  <td className="py-2 px-2 text-center text-stone-700">{item.quantity}</td>
                  <td className="py-2 px-2 text-center text-stone-700">{item.unit}</td>
                  <td className="py-2 px-2 text-right text-stone-700">{item.price.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="py-2 px-2 text-right font-semibold text-stone-900">{item.item_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-900">
                <td colSpan="4"></td>
                <td className="py-2.5 px-2 text-right text-xs font-bold text-stone-800">Total Excl. GST</td>
                <td className="py-2.5 px-2 text-right font-bold text-stone-900">{q.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
              <tr className="border-b border-stone-200">
                <td colSpan="4"></td>
                <td className="py-2 px-2 text-right text-xs font-bold text-stone-800">GST Total</td>
                <td className="py-2 px-2 text-right font-bold text-stone-900">{q.gst_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
              {q.buyback_items && q.buyback_items.length > 0 && (
                <>
                  <tr className="border-b border-stone-200">
                    <td colSpan="4"></td>
                    <td className="py-2 px-2 text-right text-xs font-bold text-red-600">Less: Buyback</td>
                    <td className="py-2 px-2 text-right font-bold text-red-600">-{(q.buyback_subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {(q.buyback_gst_total || 0) > 0 && (
                    <tr className="border-b border-stone-200">
                      <td colSpan="4"></td>
                      <td className="py-2 px-2 text-right text-xs font-bold text-red-600">Less: Buyback GST</td>
                      <td className="py-2 px-2 text-right font-bold text-red-600">-{q.buyback_gst_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                </>
              )}
              <tr className="bg-stone-50">
                <td colSpan="4"></td>
                <td className="py-3 px-2 text-right text-sm font-bold text-stone-900">Grand Total</td>
                <td className="py-3 px-2 text-right text-sm font-bold text-stone-900">{q.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Payment info for PI */}
        {q.payment_percentage !== undefined && q.payment_percentage < 100 && (
          <div className="mb-5 p-4 bg-[#002FA7]/5 border border-[#002FA7]/20">
            <p className="text-sm font-bold text-[#002FA7]">
              Payment Required: {q.payment_percentage}% = Rs. {(q.payment_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
          </div>
        )}

        {/* Bank Details for PI */}
        {company && (company.bank_name || company.bank_account_number) && apiPath === 'proforma-invoices' && (
          <div className="mb-6 p-4 border border-stone-200 bg-stone-50">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Bank Details</h3>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {company.bank_name && <p className="text-stone-600">Bank: <span className="font-semibold text-stone-900">{company.bank_name}</span></p>}
              {company.bank_account_number && <p className="text-stone-600">A/c No.: <span className="font-semibold text-stone-900">{company.bank_account_number}</span></p>}
              {company.bank_ifsc && <p className="text-stone-600">IFSC: <span className="font-semibold text-stone-900">{company.bank_ifsc}</span></p>}
              {company.bank_branch && <p className="text-stone-600">Branch: <span className="font-semibold text-stone-900">{company.bank_branch}</span></p>}
            </div>
          </div>
        )}

        {/* T&C */}
        {q.terms_conditions && (
          <div className="mb-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2">Terms & Conditions</h3>
            <div className="text-xs text-stone-600 space-y-1 whitespace-pre-line">{q.terms_conditions}</div>
          </div>
        )}

        {/* Signature */}
        <div className="mt-8 pt-4">
          <p className="text-sm text-stone-600 mb-1">Yours Faithfully,</p>
          {company && <p className="text-sm text-stone-600 mb-6">For {company.name.toUpperCase()}</p>}
          <p className="text-sm font-bold text-stone-900">{q.user_name.toUpperCase()}</p>
          {q.user_phone && <p className="text-xs text-stone-500">Phone: {q.user_phone}</p>}
        </div>
      </div>
    </div>
  );
}
